/**
 * Cloud file/folder browser. Lets the user navigate folders on
 * Google Drive / OneDrive / Dropbox and select files for import.
 *
 * Folders are clickable (navigate in) — never selectable themselves.
 * Files have a checkbox. Selection is preserved across folder changes via
 * the parent-controlled `selected` prop.
 */

import { useEffect, useState } from 'react';
import { ChevronRight, Folder, FileText, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { importService, CloudFile, FileRef, ImportSource } from '@/services/api/import';

interface Crumb { id: string; name: string }

export default function CloudFileBrowser({
  source,
  selected,
  onChange,
}: {
  source: ImportSource;
  selected: FileRef[];
  onChange: (files: FileRef[]) => void;
}) {
  const provider = (source === 'gdrive' ? 'gdrive' : source === 'onedrive' ? 'onedrive' : 'dropbox') as 'gdrive' | 'onedrive' | 'dropbox';
  const rootPath = provider === 'dropbox' ? '' : 'root';

  const [path, setPath] = useState<string>(rootPath);
  const [crumbs, setCrumbs] = useState<Crumb[]>([{ id: rootPath, name: 'Root' }]);
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedIds = new Set(selected.map(s => s.fileId || s.path).filter(Boolean) as string[]);

  useEffect(() => { void load(path); /* eslint-disable-next-line */ }, [path]);

  const load = async (p: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await importService.listCloudFiles(provider, p);
      setFiles(res.files || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'List failed');
    } finally {
      setLoading(false);
    }
  };

  const enterFolder = (f: CloudFile) => {
    const next = provider === 'dropbox' ? (f.path || `/${f.name}`) : f.id;
    setPath(next);
    setCrumbs([...crumbs, { id: next, name: f.name }]);
  };

  const goCrumb = (idx: number) => {
    const newCrumbs = crumbs.slice(0, idx + 1);
    setCrumbs(newCrumbs);
    setPath(newCrumbs[newCrumbs.length - 1].id);
  };

  const toggle = (f: CloudFile) => {
    const key = provider === 'dropbox' ? f.path! : f.id;
    const isSelected = selectedIds.has(key);
    if (isSelected) {
      onChange(selected.filter(s => (s.fileId || s.path) !== key));
    } else {
      const ref: FileRef = provider === 'dropbox'
        ? { path: f.path!, name: f.name, size: f.size, mimeType: f.mimeType }
        : { fileId: f.id, name: f.name, size: f.size, mimeType: f.mimeType };
      onChange([...selected, ref]);
    }
  };

  const selectAllVisible = () => {
    const additions: FileRef[] = [];
    for (const f of files) {
      if (f.isFolder) continue;
      const key = provider === 'dropbox' ? f.path! : f.id;
      if (selectedIds.has(key)) continue;
      additions.push(provider === 'dropbox'
        ? { path: f.path!, name: f.name, size: f.size, mimeType: f.mimeType }
        : { fileId: f.id, name: f.name, size: f.size, mimeType: f.mimeType });
    }
    if (additions.length) onChange([...selected, ...additions]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 text-xs flex-wrap">
          {crumbs.length > 1 && (
            <Button variant="ghost" size="sm" className="h-7 gap-1" onClick={() => goCrumb(crumbs.length - 2)}>
              <ArrowLeft className="h-3.5 w-3.5" /> Up
            </Button>
          )}
          {crumbs.map((c, i) => (
            <span key={`${c.id}-${i}`} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              <button
                onClick={() => goCrumb(i)}
                className={i === crumbs.length - 1 ? 'font-semibold' : 'text-muted-foreground hover:underline'}
              >
                {c.name}
              </button>
            </span>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={selectAllVisible} disabled={loading || files.every(f => f.isFolder)}>
          Select all visible
        </Button>
      </div>

      {error && <div className="rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-xs text-rose-600">{error}</div>}

      <div className="border rounded-xl overflow-hidden">
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-muted-foreground" /></div>
          ) : files.length === 0 ? (
            <div className="py-12 text-center text-xs text-muted-foreground">Folder is empty</div>
          ) : (
            <div className="divide-y">
              {files.map(f => {
                const key = provider === 'dropbox' ? (f.path || f.id) : f.id;
                const checked = !f.isFolder && selectedIds.has(key);
                return (
                  <div
                    key={key}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-muted/30 cursor-pointer text-xs"
                    onClick={() => f.isFolder ? enterFolder(f) : toggle(f)}
                  >
                    {f.isFolder ? (
                      <span className="w-4 flex-shrink-0" />
                    ) : (
                      <Checkbox checked={checked} onClick={(e) => e.stopPropagation()} onCheckedChange={() => toggle(f)} />
                    )}
                    {f.isFolder ? <Folder className="h-4 w-4 text-blue-500 flex-shrink-0" /> : <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                    <span className="flex-1 truncate" title={f.name}>{f.name}</span>
                    {!f.isFolder && f.size > 0 && (
                      <span className="text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
                    )}
                    {f.isFolder && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      <div className="text-xs text-muted-foreground">
        {selected.length} file{selected.length === 1 ? '' : 's'} selected total
      </div>
    </div>
  );
}
